import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faLock } from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import { parseLocalDate } from '@frontend/helpers/dateHelpers';
import AnchoredPopover from './AnchoredPopover';
import TimeRequestService, { TimeRequest } from './TimeRequestService';
import { formatRequestHours, isDayLockMessage, serverErrorMessage } from './dayLockHelpers';
import './TimesheetDayLocks.css';

export interface TimeRequestCell {
  matterId: string;
  /** e.g. "1234 - Pump Station" */
  matterLabel: string;
  costCode: string;
  costCodeLabel: string;
  /** yyyy-MM-dd */
  dateKey: string;
  /** The cell's total right now. */
  currentHours: number;
}

interface TimeRequestPopoverProps {
  anchor: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  cell: TimeRequestCell | null;
  /** The latest request already made for this cell, if any. */
  existing?: TimeRequest | null;
  /** A request was created or cancelled — refresh the request queries. */
  onChanged: () => void;
  /** The server says this tab's lock state is stale — refetch the locks. */
  onStale?: () => void;
}

function isQuarterHours(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 24 && Math.abs(value * 4 - Math.round(value * 4)) < 1e-9;
}

/**
 * The permish 2/3 request flow on a locked day (plan F1/F2): one click on the cell opens this, the
 * hours field — labelled "New total for this day" and pre-filled with the cell's current hours — has
 * focus, Enter sends and Esc closes. A pending request shows instead, with "Cancel request".
 */
const TimeRequestPopover: React.FC<TimeRequestPopoverProps> = ({
  anchor,
  open,
  onClose,
  cell,
  existing,
  onChanged,
  onStale,
}) => {
  const isPending = existing?.status === 'pending';
  const isDeclined = existing?.status === 'declined';
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !cell) return;
    const start = isDeclined && existing ? existing.requestedHours : cell.currentHours;
    setHours(start > 0 ? String(start) : '0');
    setNote('');
  }, [open, cell, existing, isDeclined]);

  if (!cell) return null;
  const dayLabel = format(parseLocalDate(cell.dateKey), 'EEE MMM d');
  const weekdayLabel = format(parseLocalDate(cell.dateKey), 'EEEE');

  const fail = (err: unknown, fallback: string) => {
    const message = serverErrorMessage(err);
    toastService.error(message || fallback);
    if (isDayLockMessage(message)) onStale?.();
    onChanged();
  };

  const submit = async () => {
    if (busy) return;
    const value = parseFloat(hours);
    if (!isQuarterHours(value)) {
      toastService.warning('Enter hours between 0 and 24 in quarter-hour steps (e.g. 1.25, 2.5).');
      return;
    }
    setBusy(true);
    try {
      await TimeRequestService.create({
        matterid: cell.matterId,
        cost_code: cell.costCode,
        work_date: cell.dateKey,
        hours: value,
        note: note.trim() || undefined,
      });
      toastService.success('Request sent to your administrators.');
      onChanged();
      onClose();
    } catch (err) {
      fail(err, 'Could not send the request.');
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    if (!existing || busy) return;
    setBusy(true);
    try {
      await TimeRequestService.cancel(existing.requestId);
      toastService.success('Request cancelled.');
      onChanged();
      onClose();
    } catch (err) {
      fail(err, 'Could not cancel the request.');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <AnchoredPopover
      anchor={anchor}
      open={open}
      onClose={onClose}
      width={300}
      ariaLabel={`Request time for ${dayLabel}`}
      className="tdl-request-popover"
    >
      <div className="tdl-popover-head">
        <FontAwesomeIcon icon={isPending ? faClock : faLock} className="tdl-popover-icon" />
        <span>
          {isPending ? 'Pending request' : 'Day locked'} — {dayLabel}
        </span>
      </div>
      <div className="tdl-popover-context">
        <span title={cell.matterLabel}>{cell.matterLabel}</span>
        <span>
          {cell.costCode}
          {cell.costCodeLabel ? ` · ${cell.costCodeLabel}` : ''}
        </span>
      </div>

      {isPending && existing ? (
        <>
          <p className="tdl-popover-text">
            You asked for a new total of <strong>{formatRequestHours(existing.requestedHours)}</strong>{' '}
            (was {formatRequestHours(existing.currentHours)}). An administrator will approve or decline it.
          </p>
          {existing.note && <p className="tdl-popover-note">“{existing.note}”</p>}
          <div className="tdl-popover-actions">
            <button type="button" className="tdl-btn tdl-btn-danger" onClick={cancelRequest} disabled={busy}>
              {busy ? 'Cancelling…' : 'Cancel request'}
            </button>
            <button type="button" className="tdl-btn" onClick={onClose} disabled={busy}>
              Close
            </button>
          </div>
        </>
      ) : (
        <>
          {isDeclined && existing && (
            <p className="tdl-popover-declined" role="status">
              Your last request was declined{existing.decisionNote ? `: ${existing.decisionNote}` : '.'}
            </p>
          )}
          <p className="tdl-popover-text">
            {weekdayLabel}s are locked. Ask an administrator to set this day's total.
          </p>
          <label className="tdl-field">
            <span>New total for this day</span>
            <input
              type="number"
              step="0.25"
              min="0"
              max="24"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              disabled={busy}
              aria-label="New total for this day"
            />
            <small>Currently {formatRequestHours(cell.currentHours)} on this day.</small>
          </label>
          <label className="tdl-field">
            <span>Note (optional)</span>
            <input
              type="text"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
              aria-label="Note for the administrator"
            />
          </label>
          <div className="tdl-popover-actions">
            <button type="button" className="tdl-btn tdl-btn-primary" onClick={submit} disabled={busy}>
              {busy ? 'Sending…' : isDeclined ? 'Request again' : 'Request'}
            </button>
            <button type="button" className="tdl-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </>
      )}
    </AnchoredPopover>
  );
};

export default TimeRequestPopover;
